import { hasValue, SortTypeEnum } from "@repo/helpers";
import { useEffect, useState } from "react";

import Grid, { IFilter, SortBy } from "../../grid";
import { Props } from "./types";

export type { PageChangeData } from "./types";

export function GridPreLoaded<T>(props: Props<T>) {
	//for setting default props
	return <GridPreLoadedInternal<T> pageSize={5} page canSetItemsPerPage {...props} />;
}

function GridPreLoadedInternal<T>(props: Props<T>) {
	const { page = true, canSetItemsPerPage, ...rest } = props;
	const sortByColumn = props.columns.filter((c) => c.sortBy !== SortTypeEnum.none)[0];
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(props.pageSize);
	const [sortedValues, setSortedValues] = useState([]);
	const [slicedValues, setSlicedValues] = useState([]);
	const [sortBy, setSortBy] = useState<SortBy>(
		sortByColumn && {
			name: String(sortByColumn.propertyName),
			order: sortByColumn.sortBy,
		},
	);
	const totalItems = props.values?.length || 0;
	const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;

	useEffect(() => {
		let values = [...props.values];
		if (sortBy && sortBy.order !== SortTypeEnum.none) {
			values = values.sort(sortData);
		}
		setSortedValues(values);
	}, [sortBy, props.values]);

	useEffect(() => {
		const itemsToSkip = pageSize * (currentPage - 1);
		const values = sortedValues.slice(itemsToSkip, itemsToSkip + pageSize);
		setSlicedValues(values);
	}, [pageSize, currentPage, sortedValues]);

	function onGetData(page: number, currentFilters: IFilter) {
		onPageChanged(page);
		if (hasValue(currentFilters?.sortBy)) {
			setSortBy(currentFilters.sortBy);
		}
		if (page === currentPage && !currentFilters) props.getData(page, currentFilters);
	}

	function onPageChanged(page: number) {
		setCurrentPage(page);
		props.onPageChanged?.({
			currentPage: page,
			totalPages,
			totalItems,
			pageSize,
		});
	}

	function sortData(a: T, b: T) {
		const aProp = a[sortBy.name];
		const bProp = b[sortBy.name];

		let comparison: number;
		if (typeof aProp === "string") {
			comparison = ("" + aProp).localeCompare(bProp);
		} else if (aProp === bProp) {
			comparison = 0;
		} else {
			comparison = aProp < bProp ? -1 : 1;
		}
		return sortBy.order === SortTypeEnum.ascending ? comparison : comparison * -1;
	}

	return (
		<Grid
			{...props}
			values={slicedValues}
			totalItems={totalItems}
			currentPage={currentPage}
			pageChanged={onPageChanged}
			pageSize={pageSize}
			getData={onGetData}
			setItemsPerPage={setPageSize}
			canSetItemsPerPage={props.canSetItemsPerPage}
		/>
	);
}
